import { useMemo } from 'react';
import { View, ProductAggregation } from './types';
import { OrderHistory } from '../../stores/appOrderHistoryStore';
import { toDate, withinPeriod, periodLabel, startOfPeriod } from './utils';
import { aggregateQuantityInfos, formatAggregatedQuantity, FormattedQuantity, getPieceUnitLabel } from '../../utils/quantityUtils';
import { Unit } from '../../stores/appOrderHistoryStore';
import { getOrderTotal, getProductPrice } from './vatUtils';
import { PriceAlert } from '../../services/priceAlerts.ts/priceAlertsService';
import { getPriceAlertForProduct } from '../../utils/priceAlertUtils';
import { resolveProductIdentifier, getProductQuantity } from '../../utils/platformUtils';

export { periodLabel };

/**
 * Average per-period quantities in BASE units (g / ml / pieces), then format ONCE at the end.
 *
 * Averaging the already-formatted values was wrong: `formatAggregatedQuantity` auto-scales g→kg (and
 * ml→L) at ≥1000, so a product ordered 1000 g in one period ({value: 1, unit: 'kg'}) and 220 g in the
 * next ({value: 220, unit: 'g'}) averaged to (1 + 220) / 2 = 110.5, labelled with whichever unit the
 * FIRST period happened to have. Any product crossing 1 kg / 1 L between periods was affected.
 */
function averagePeriodQuantities(
  periods: Array<{ base: number; stdUnit: Unit }>,
  category?: string
): FormattedQuantity | undefined {
  if (periods.length === 0) return undefined;

  // A product has one std unit; if periods disagree (mixed-unit data) use the most common one,
  // mirroring how aggregateQuantityInfos resolves the same conflict within a period.
  const unitCounts = new Map<Unit, number>();
  periods.forEach(p => unitCounts.set(p.stdUnit, (unitCounts.get(p.stdUnit) ?? 0) + 1));
  const stdUnit = Array.from(unitCounts.entries()).sort((a, b) => b[1] - a[1])[0][0];

  const matching = periods.filter(p => p.stdUnit === stdUnit);
  const averageBase = matching.reduce((sum, p) => sum + p.base, 0) / matching.length;

  return formatAggregatedQuantity(
    averageBase,
    { weightedStdAmount: 1, stdUnit, totalRawQuantity: averageBase, hasQuantityData: true },
    category
  );
}

/**
 * Canonical product-aggregation key: `${resolvedIdentifier}-${platform}`. Grouping/dedup rules live in
 * `resolveProductIdentifier` (sentinel article ids, name-keyed platforms, reused "restgroep" ids).
 * The platform suffix keeps the same articleId on two suppliers apart.
 * Returns null when there is no usable identifier (caller should skip the product).
 */
export function getProductAggregationKey(
  product: { articleId?: string; name?: string },
  platform: string,
  supplierName?: string
): { key: string; identifier: string } | null {
  const identifier = resolveProductIdentifier(platform, product.articleId, product.name);
  if (!identifier) return null;
  return { key: supplierName !== undefined && platform === 'any_invoice'
    ? JSON.stringify([identifier, platform, supplierName.trim().toLowerCase()])
    : `${identifier}-${platform}`, identifier };
}


export function supplierSplitAgg(
  orders: OrderHistory[] = [],
  start: Date,
  view: View,
  showExVat: boolean = true
): { platform: string; value: number }[] {
  const totals: Record<string, number> = {};

  orders.forEach(o => {
    const od = toDate(o.orderDate);
    if (!withinPeriod(od, start, view)) return;
    // Use getOrderTotal to respect VAT toggle
    totals[o.platform] = (totals[o.platform] || 0) + getOrderTotal(o, showExVat);
  });

  return Object.entries(totals).map(([platform, value]) => ({ platform, value }));
}

function calculateAverageSpent(
  orders: OrderHistory[],
  identifier: string,
  platform: string,
  view: View,
  showExVat: boolean = true,
  currentPeriodStart: Date
): number {
  // Group orders by period and calculate spend for each period
  // Only include periods up to and including the current period (no future data)
  const periodSpends = new Map<string, number>();

  orders.forEach(o => {
    const orderDate = toDate(o.orderDate);
    // Skip orders after the current period being viewed
    if (orderDate > currentPeriodStart) return;

    // Only process orders from the same platform
    if (o.platform !== platform) return;

    const periodStart = startOfPeriod(orderDate, view);
    const periodKey = periodStart.toISOString();

    (o.productData || []).forEach(p => {
      if (resolveProductIdentifier(o.platform, p.articleId, p.name) === identifier) {
        const price = getProductPrice(p, showExVat);
        const qty = getProductQuantity(p, o.platform);
        const spend = price * qty;
        periodSpends.set(periodKey, (periodSpends.get(periodKey) || 0) + spend);
      }
    });
  });

  const spends = Array.from(periodSpends.values()).filter(spend => spend > 0);
  if (spends.length === 0) return 0;

  return spends.reduce((sum, spend) => sum + spend, 0) / spends.length;
}

function calculateAverageQuantity(
  orders: OrderHistory[],
  identifier: string,
  platform: string,
  view: View,
  currentPeriodStart: Date,
  category?: string
): FormattedQuantity | undefined {
  // Group orders by period and collect product data for each period
  // Only include periods up to and including the current period (no future data)
  const periodProductData = new Map<string, Array<{
    rawQuantity: number;
    quantityInfo?: any;
    articleId: string;
    platform: string;
    name: string;
  }>>();

  const productKey = `${identifier}-${platform}`;

  orders.forEach(o => {
    const orderDate = toDate(o.orderDate);
    // Skip orders after the current period being viewed
    if (orderDate > currentPeriodStart) return;

    // Only process orders from the same platform
    if (o.platform !== platform) return;

    const periodStart = startOfPeriod(orderDate, view);
    const periodKey = periodStart.toISOString();

    (o.productData || []).forEach(p => {
      if (resolveProductIdentifier(o.platform, p.articleId, p.name) === identifier) {

        if (!periodProductData.has(periodKey)) {
          periodProductData.set(periodKey, []);
        }

        periodProductData.get(periodKey)!.push({
          rawQuantity: p.rawQuantity || 0,
          quantityInfo: p.quantityInfo,
          articleId: p.articleId,
          platform: o.platform,
          name: p.name
        });
      }
    });
  });
  
  // Collect each period's quantity in BASE units; format once after averaging (see averagePeriodQuantities).
  const periodBaseQuantities: Array<{ base: number; stdUnit: Unit }> = [];

  periodProductData.forEach((productDataArray) => {
    if (productDataArray.length > 0) {
      const periodAggregatedInfo = aggregateQuantityInfos(productDataArray, productKey);
      if (periodAggregatedInfo.hasQuantityData) {
        periodBaseQuantities.push({
          base: periodAggregatedInfo.totalRawQuantity * periodAggregatedInfo.weightedStdAmount,
          stdUnit: periodAggregatedInfo.stdUnit,
        });
      }
    }
  });

  return averagePeriodQuantities(periodBaseQuantities, category);
}

/**
 * Pre-computed analytics hook that calculates all product averages ONCE per period.
 * Uses a SINGLE PASS algorithm through all orders for maximum performance.
 * This eliminates the performance bottleneck of recalculating averages for every product on every render.
 *
 * @returns Maps of pre-computed average spent (ex VAT & inc VAT) and average quantity for all products
 */
export function usePrecomputedProductAnalytics(
  allOrders: OrderHistory[],
  startDate: Date,
  view: View,
  separateSuppliers: boolean = false
) {
  return useMemo(() => {

    const avgSpentExVat = new Map<string, number>();
    const avgSpentIncVat = new Map<string, number>();
    const avgQuantity = new Map<string, FormattedQuantity | undefined>();

    // Maps to track period-level data for each product
    // key -> periodKey -> spend/quantity data
    const periodSpendsExVat = new Map<string, Map<string, number>>();
    const periodSpendsIncVat = new Map<string, Map<string, number>>();
    const periodProductData = new Map<string, Map<string, Array<{
      rawQuantity: number;
      quantityInfo?: any;
      articleId: string;
      platform: string;
      name: string;
    }>>>();

    // Track category per product key for unit label display
    const productCategoryMap = new Map<string, string>();

    // Cache resolved identifiers: the same (platform, articleId, name) triple recurs on every order,
    // and resolving normalizes the name — worth memoizing in this single-pass hot loop.
    const identifierCache = new Map<string, string | null>();
    const getIdentifier = (platform: string, articleId?: string, name?: string): string | null => {
      const cacheKey = `${platform} ${articleId ?? ''} ${name ?? ''}`;
      let resolved = identifierCache.get(cacheKey);
      if (resolved === undefined) {
        resolved = resolveProductIdentifier(platform, articleId, name);
        identifierCache.set(cacheKey, resolved);
      }
      return resolved;
    };

    // Cache period keys to avoid repeated Date operations
    const periodKeyCache = new Map<number, string>();
    const getPeriodKey = (orderDate: Date): string => {
      const timestamp = orderDate.getTime();
      if (!periodKeyCache.has(timestamp)) {
        const periodStart = startOfPeriod(orderDate, view);
        periodKeyCache.set(timestamp, periodStart.toISOString());
      }
      return periodKeyCache.get(timestamp)!;
    };

    // SINGLE PASS: Build all period data for all products at once
    allOrders.forEach(o => {
      const orderDate = toDate(o.orderDate);
      // Skip orders after the current period being viewed
      if (orderDate > startDate) return;

      const periodKey = getPeriodKey(orderDate);

      const productData = o.productData;
      if (!productData || productData.length === 0) return;

      productData.forEach(p => {
        const identifier = getIdentifier(o.platform, p.articleId, p.name);
        if (!identifier) return;

        const productKey = separateSuppliers && o.platform === 'any_invoice'
          ? JSON.stringify([identifier, o.platform, (o.unknownSupplierName ?? '').trim().toLowerCase()])
          : `${identifier}-${o.platform}`;

        // Initialize maps if needed (using ?? for cleaner code)
        let periodSpendsExVatMap = periodSpendsExVat.get(productKey);
        let periodSpendsIncVatMap = periodSpendsIncVat.get(productKey);
        let periodDataMap = periodProductData.get(productKey);

        if (!periodSpendsExVatMap) {
          periodSpendsExVatMap = new Map();
          periodSpendsIncVatMap = new Map();
          periodDataMap = new Map();
          periodSpendsExVat.set(productKey, periodSpendsExVatMap);
          periodSpendsIncVat.set(productKey, periodSpendsIncVatMap);
          periodProductData.set(productKey, periodDataMap);
          if (p.category) productCategoryMap.set(productKey, p.category);
        }

        // Track spending for both VAT versions
        const priceExVat = getProductPrice(p, true);
        const priceIncVat = getProductPrice(p, false);
        const qty = getProductQuantity(p, o.platform);

        const spendExVat = priceExVat * qty;
        const spendIncVat = priceIncVat * qty;

        periodSpendsExVatMap!.set(periodKey, (periodSpendsExVatMap!.get(periodKey) || 0) + spendExVat);
        periodSpendsIncVatMap!.set(periodKey, (periodSpendsIncVatMap!.get(periodKey) || 0) + spendIncVat);

        // Track product data for quantity calculation
        if (!periodDataMap!.has(periodKey)) {
          periodDataMap!.set(periodKey, []);
        }
        periodDataMap!.get(periodKey)!.push({
          rawQuantity: p.rawQuantity || 0,
          quantityInfo: p.quantityInfo,
          articleId: p.articleId,
          platform: o.platform,
          name: p.name
        });
      });
    });

    // Calculate averages from the accumulated period data
    periodSpendsExVat.forEach((periodMap, productKey) => {
      const spends = Array.from(periodMap.values()).filter(spend => spend > 0);
      avgSpentExVat.set(productKey, spends.length > 0 ? spends.reduce((sum, spend) => sum + spend, 0) / spends.length : 0);
    });

    periodSpendsIncVat.forEach((periodMap, productKey) => {
      const spends = Array.from(periodMap.values()).filter(spend => spend > 0);
      avgSpentIncVat.set(productKey, spends.length > 0 ? spends.reduce((sum, spend) => sum + spend, 0) / spends.length : 0);
    });

    // Calculate average quantities
    periodProductData.forEach((periodMap, productKey) => {
      const periodBaseQuantities: Array<{ base: number; stdUnit: Unit }> = [];
      const category = productCategoryMap.get(productKey);

      periodMap.forEach((productDataArray) => {
        if (productDataArray.length > 0) {
          const periodAggregatedInfo = aggregateQuantityInfos(productDataArray, productKey);
          if (periodAggregatedInfo.hasQuantityData) {
            periodBaseQuantities.push({
              base: periodAggregatedInfo.totalRawQuantity * periodAggregatedInfo.weightedStdAmount,
              stdUnit: periodAggregatedInfo.stdUnit,
            });
          }
        }
      });

      avgQuantity.set(productKey, averagePeriodQuantities(periodBaseQuantities, category));
    });


    return { avgSpentExVat, avgSpentIncVat, avgQuantity };
  }, [allOrders, startDate, view, separateSuppliers]); // ← only recompute when period changes
}


export function calculateUnitPrice(
  price: number,
  quantityInfo?: any,
  category?: string
): { value: number; unit: string; displayText: string } | undefined {
  if (!quantityInfo || !quantityInfo.stdAmount || !quantityInfo.stdMultiplier || !quantityInfo.stdUnit) {
    return undefined;
  }

  // Since we now adjust the price at source to be per quantityInfo unit,
  // we just need to calculate the unit price based on the quantityInfo structure
  const totalQuantity = quantityInfo.stdAmount * quantityInfo.stdMultiplier;
  
  // Calculate unit price per individual unit (e.g., per gram, per ml, per piece)
  const unitPriceValue = price / totalQuantity;
  
  // Convert unit for display (convert to larger units where appropriate)
  let displayUnit = quantityInfo.stdUnit;
  let finalUnitPrice = unitPriceValue;
  
  // Convert ml to L if appropriate
  if (quantityInfo.stdUnit === Unit.MILLILITER) {
    finalUnitPrice = unitPriceValue * 1000; // Price per liter
    displayUnit = 'L';
  }
  // Convert g to kg if appropriate  
  else if (quantityInfo.stdUnit === Unit.GRAM) {
    finalUnitPrice = unitPriceValue * 1000; // Price per kg
    displayUnit = 'kg';
  }
  // Keep piece as is (use bottle label for wine)
  else if (quantityInfo.stdUnit === Unit.PIECE) {
    const pieceLabel = getPieceUnitLabel(category);
    displayUnit = pieceLabel.short;
  }

  // Format the display text
  const formatNumber = (num: number): string => {
    const rounded = Math.round(num * 100) / 100; // Round to 2 decimal places
    return rounded.toFixed(2);
  };

  return {
    value: finalUnitPrice,
    unit: displayUnit,
    displayText: `${formatNumber(finalUnitPrice)}/${displayUnit}`
  };
}

export function topProductsAgg(
  orders: OrderHistory[] = [],
  start: Date,
  view: View,
  allOrders: OrderHistory[] = orders,
  showExVat: boolean = true,
  priceAlerts: PriceAlert[] = []
): ProductAggregation[] {
  const map = new Map<string, ProductAggregation>();
  const productDataMap = new Map<string, Array<{
    rawQuantity: number;
    quantityInfo?: any;
    articleId: string;
    platform: string;
    name: string;
  }>>();
  const productRestaurantsMap = new Map<string, Map<string, string>>();

  // PERFORMANCE OPTIMIZATION: Pre-calculate all unique product keys first
  // This avoids calling calculateAverageSpent/Quantity for each product individually
  const uniqueProductKeys = new Set<string>();
  const categoryLookup = new Map<string, string>();

  orders.forEach(o => {
    if (!withinPeriod(toDate(o.orderDate), start, view)) return;

    (o.productData || []).forEach(p => {
      const keyInfo = getProductAggregationKey(p, o.platform);
      if (!keyInfo) return;
      uniqueProductKeys.add(keyInfo.key);
      if (p.category) categoryLookup.set(keyInfo.key, p.category);
    });
  });

  // PERFORMANCE OPTIMIZATION: Pre-calculate averages for all unique products in one pass
  // Build lookup maps instead of calculating per-product
  const averageSpentLookup = new Map<string, number>();
  const averageQuantityLookup = new Map<string, FormattedQuantity | undefined>();

  uniqueProductKeys.forEach(key => {
    // Safe split on the LAST '-': the identifier may itself contain '-' (real article ids like kobier
    // 'CH-2770' pass through verbatim), but PLATFORM names never do, so the final '-' is the separator.
    const lastDashIndex = key.lastIndexOf('-');
    const identifier = key.substring(0, lastDashIndex);
    const platform = key.substring(lastDashIndex + 1);

    averageSpentLookup.set(key, calculateAverageSpent(allOrders, identifier, platform, view, showExVat, start));
    averageQuantityLookup.set(key, calculateAverageQuantity(allOrders, identifier, platform, view, start, categoryLookup.get(key)));
  });

  // Now build the aggregation map using the pre-calculated values
  // Iterate oldest-first so the newest order's price wins the last-write
  [...orders].reverse().forEach(o => {
    if (!withinPeriod(toDate(o.orderDate), start, view)) return;

    (o.productData || []).forEach(p => {
      const keyInfo = getProductAggregationKey(p, o.platform);
      if (!keyInfo) return;
      const { key, identifier } = keyInfo;

      if (!map.has(key)) {
        const price = getProductPrice(p, showExVat);
        map.set(key, {
          articleId: identifier,
          name: p.name,
          qty: 0,
          spend: 0,
          platform: o.platform,
          category: p.category,
          mainCategory: p.mainCategory,
          subCategories: p.subCategories || [],
          price: price || 0,
          quantityInfo: p.quantityInfo, // Store the quantityInfo for unit price calculation
          averageSpent: averageSpentLookup.get(key) || 0,
          averageQuantity: averageQuantityLookup.get(key)?.displayText ?? "--",
          priceAlert: getPriceAlertForProduct(priceAlerts, identifier, o.platform, start, view),
          taxRate: p.taxRate,
          taxCalculationMethod: o.taxCalculationMethod,
          restaurantIds: [],
          restaurantNames: []
        });
        productDataMap.set(key, []);
        productRestaurantsMap.set(key, new Map<string, string>());
      }

      // Track restaurants for this product
      if (o.restaurantId && o.restaurantName) {
        const restaurantsMap = productRestaurantsMap.get(key)!;
        restaurantsMap.set(o.restaurantId, o.restaurantName);
      }

      // Collect all product data for quantity aggregation
      productDataMap.get(key)!.push({
        rawQuantity: p.rawQuantity || 0,
        quantityInfo: p.quantityInfo,
        articleId: p.articleId,
        platform: o.platform,
        name: p.name
      });

      const r = map.get(key)!;
      r.qty += (p.quantity || 0);
      const price = getProductPrice(p, showExVat);
      const qty = getProductQuantity(p, o.platform);
      r.spend += price * qty;
      if (price) {
        r.price = price;
        r.quantityInfo = p.quantityInfo; // Keep quantityInfo in sync with price
      }
    });
  });

  // Calculate aggregated quantity info for each product and populate restaurant lists
  productDataMap.forEach((productDataArray, key) => {
    const aggregation = map.get(key);
    if (aggregation) {
      aggregation.aggregatedQuantityInfo = aggregateQuantityInfos(productDataArray, key);
      aggregation.formattedQuantity = formatAggregatedQuantity(aggregation.aggregatedQuantityInfo.totalRawQuantity, aggregation.aggregatedQuantityInfo, aggregation.category)?.displayText
      // Calculate unit price using the stored quantityInfo
      if (aggregation.price && aggregation.quantityInfo) {
        aggregation.unitPrice = calculateUnitPrice(aggregation.price, aggregation.quantityInfo, aggregation.category);
      }
      // Populate restaurant lists from the map
      const restaurantsMap = productRestaurantsMap.get(key);
      if (restaurantsMap) {
        aggregation.restaurantIds = Array.from(restaurantsMap.keys());
        aggregation.restaurantNames = Array.from(restaurantsMap.values());
      }
    }
  });


  return Array.from(map.values()).sort((a, b) => b.spend - a.spend);
}

/**
 * Fast aggregation function that uses pre-computed average maps instead of recalculating.
 * This is 30-100x faster than the old approach for large datasets.
 *
 * @param orders - Filtered orders for the current period
 * @param averageSpentMap - Pre-computed average spent map (either ex VAT or inc VAT)
 * @param avgQuantityMap - Pre-computed average quantity map
 * @param priceAlerts - Price alerts for the current period
 * @param startDate - Start date of the current period
 * @param view - Current view (day, week, month, year)
 * @param showExVat - Whether to show prices ex VAT (true) or inc VAT (false)
 * @returns Aggregated product data sorted by spend
 */
export function fastTopProductsAggregation(
  orders: OrderHistory[],
  averageSpentMap: Map<string, number>,
  avgQuantityMap: Map<string, FormattedQuantity | undefined>,
  priceAlerts: PriceAlert[],
  startDate: Date,
  view: View,
  showExVat: boolean = true,
  separateSuppliers: boolean = false
): ProductAggregation[] {

  const map = new Map<string, ProductAggregation>();
  const productDataMap = new Map<string, Array<{
    rawQuantity: number;
    quantityInfo?: any;
    articleId: string;
    platform: string;
    name: string;
  }>>();
  const productRestaurantsMap = new Map<string, Map<string, string>>();

  // Build aggregation map - no more expensive per-product calculations!
  // Iterate oldest-first so the newest order's price wins the last-write
  [...orders].reverse().forEach(o => {
    (o.productData || []).forEach(p => {
      const keyInfo = getProductAggregationKey(p, o.platform, separateSuppliers ? o.unknownSupplierName ?? '' : undefined);
      if (!keyInfo) return;
      const { key, identifier } = keyInfo;

      if (!map.has(key)) {
        const price = getProductPrice(p, showExVat);

        map.set(key, {
          articleId: identifier,
          name: p.name,
          qty: 0,
          spend: 0,
          platform: o.platform,
          category: p.category,
          mainCategory: p.mainCategory,
          subCategories: p.subCategories || [],
          price: price || 0,
          quantityInfo: p.quantityInfo,
          // Use pre-computed values from the maps - INSTANT lookup!
          averageSpent: averageSpentMap.get(key) || 0,
          averageQuantity: avgQuantityMap.get(key)?.displayText ?? "--",
          priceAlert: getPriceAlertForProduct(priceAlerts, identifier, o.platform, startDate, view),
          taxRate: p.taxRate,
          taxCalculationMethod: o.taxCalculationMethod,
          restaurantIds: [],
          restaurantNames: [],
          unknownSupplierName: o.unknownSupplierName
        });
        productDataMap.set(key, []);
        productRestaurantsMap.set(key, new Map<string, string>());
      }

      // Track restaurants for this product
      if (o.restaurantId && o.restaurantName) {
        const restaurantsMap = productRestaurantsMap.get(key)!;
        restaurantsMap.set(o.restaurantId, o.restaurantName);
      }

      // Collect all product data for quantity aggregation
      productDataMap.get(key)!.push({
        rawQuantity: p.rawQuantity || 0,
        quantityInfo: p.quantityInfo,
        articleId: p.articleId,
        platform: o.platform,
        name: p.name
      });

      const r = map.get(key)!;
      r.qty += (p.quantity || 0);
      const price = getProductPrice(p, showExVat);
      const qty = getProductQuantity(p, o.platform);
      r.spend += price * qty;
      if (price) {
        r.price = price;
        r.quantityInfo = p.quantityInfo;
      }
    });
  });

  // Calculate aggregated quantity info for each product and populate restaurant lists
  productDataMap.forEach((productDataArray, key) => {
    const aggregation = map.get(key);
    if (aggregation) {
      aggregation.aggregatedQuantityInfo = aggregateQuantityInfos(productDataArray, key);
      aggregation.formattedQuantity = formatAggregatedQuantity(
        aggregation.aggregatedQuantityInfo.totalRawQuantity,
        aggregation.aggregatedQuantityInfo,
        aggregation.category
      )?.displayText;

      // Calculate unit price using the stored quantityInfo
      if (aggregation.price && aggregation.quantityInfo) {
        aggregation.unitPrice = calculateUnitPrice(aggregation.price, aggregation.quantityInfo, aggregation.category);
      }

      // Populate restaurant lists from the map
      const restaurantsMap = productRestaurantsMap.get(key);
      if (restaurantsMap) {
        aggregation.restaurantIds = Array.from(restaurantsMap.keys());
        aggregation.restaurantNames = Array.from(restaurantsMap.values());
      }
    }
  });


  return Array.from(map.values()).sort((a, b) => b.spend - a.spend);
}

export function ordersInPeriod(
  orders: OrderHistory[] = [],
  start: Date,
  view: View,
  customStartDate?: Date | null,
  customEndDate?: Date | null
): OrderHistory[] {
  return orders
    .filter(o => withinPeriod(toDate(o.orderDate), start, view, customStartDate, customEndDate))
    .sort((a, b) => +toDate(b.orderDate) - +toDate(a.orderDate));
}